import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { BehaviorSubject, Observable, Subject, Subscriber, combineLatest, of } from 'rxjs';
import { filter, map, startWith, switchMap, takeUntil } from 'rxjs/operators';

import {
  DashboardHeatingOperatingModeValue,
  DeviceConnectionStatus,
  DeviceConnectionStatusValues,
  Feature,
  FeatureCommands,
  FeatureDeprecated,
  FeatureNames,
  IsActiveProperty,
  IsStringValueProperty,
  RenameHeatingCircuitCommands,
  RenameHeatingCircuitProperties,
} from '@vi-iot/client';
import { ViToastService } from '@vi-iot/ui';

import { NO_VALUE } from '@pipes';
import { DataService } from '@services';
import {
  DevicesStore,
  GatewayStore,
  InstallationFeaturesStore,
  RemoteDeviceType,
  UnassignableDeviceDisconnected,
  isReady,
} from '@store';

import { MissedHcAssignmentWarningModalComponent } from '../installation/missed-hc-assignment-warning-modal/missed-hc-assignment-warning-modal.component';
import { HeatingCircuitHelper } from '../installation/heating-circuit-features-panel/heating-circuit.helper';

export interface Firmware {
  name: string;
  value: {
    value: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class InstallationService {
  missedHCAssignmentDialogRef: MatDialogRef<MissedHcAssignmentWarningModalComponent>;

  selectedCircuitId$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  selectedRefrigerantCircuitId$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  selectedRoomId$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  selectedOtherRoomId$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  selectedFhtDeviceId$: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  firstCircuitId$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  activeCircuitId$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  installationId$: BehaviorSubject<number> = new BehaviorSubject<number>(null);
  gatewaySerial$: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  isSrcActive$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  ngUnsubscribe: Subject<void> = new Subject<void>();
  defrostingWasStartedManually = false;

  constructor(
    public translation: TranslateService,
    private dataService: DataService,
    private viToastService: ViToastService,
    private devicesStore: DevicesStore,
    private dialog: MatDialog,
    private installationFeaturesStore: InstallationFeaturesStore,
    private gatewayStore: GatewayStore
  ) {}

  showDeregisterInstallationError(): void {
    this.viToastService.error({
      message: this.translation.instant('DEREGISTER_INSTALLATION_MODAL.SNACKBAR.ERROR_MSG'),
    });
  }

  showDeregisterInstallationSuccess(): void {
    this.viToastService.info({
      message: this.translation.instant('DEREGISTER_INSTALLATION_MODAL.SNACKBAR.SUCCESS_MSG'),
    });
  }

  showDeregisterGatewayError(): void {
    this.viToastService.error({
      message: this.translation.instant('DEREGISTER_GATEWAY_MODAL.SNACKBAR.ERROR_MSG'),
    });
  }

  showDeregisterGatewaySuccess(): void {
    this.viToastService.info({
      message: this.translation.instant('DEREGISTER_GATEWAY_MODAL.SNACKBAR.SUCCESS_MSG'),
    });
  }

  openMissedHCAssignmentDialog(): Observable<any> {
    if (this.missedHCAssignmentDialogRef) {
      this.missedHCAssignmentDialogRef.close();
    }

    this.missedHCAssignmentDialogRef = this.dialog.open(MissedHcAssignmentWarningModalComponent, {
      width: '535px',
      panelClass: 'missed-hc-assignment-dialog',
      disableClose: true,
    });

    return this.missedHCAssignmentDialogRef.afterClosed();
  }

  resetInstallation(): void {
    this.firstCircuitId$.next(null);
    this.activeCircuitId$.next(null);
    this.installationId$.next(null);
    this.gatewaySerial$.next(null);
  }

  setInstallationId(id: number): void {
    this.resetInstallation();
    this.dataService.selectedInstallationId = id;
  }

  resolveActiveHolidayModeState(startDate: Date, endDate: Date): boolean {
    return moment(new Date()).isBetween(startDate, endDate, 'day');
  }

  getCallForHeatHeatingZoneName(index: string | null): string {
    if (index === null) {
      return this.translation.instant('+INSTALLATIONS.INSTALLATION.PANEL.CALL_FOR_HEAT');
    } else {
      return (
        `${index}. ` + this.translation.instant('+INSTALLATIONS.INSTALLATION.PANEL.CALL_FOR_HEAT')
      );
    }
  }

  getCircuitName(circuitId: string, nameWithoutIndex?: boolean): Observable<string> {
    if (circuitId == null) {
      return of(NO_VALUE);
    }

    let displayedCircuitId: number;
    let defaultName: string;

    const heatingZoneCircuits = [4, 5, 6];
    const isHeatingZone = heatingZoneCircuits.includes(+circuitId);
    if (isHeatingZone) {
      // circuit of index 4 is TT circuit and we want to display it as 1. Heating Zone
      displayedCircuitId = +circuitId - 3;
      defaultName =
        `${displayedCircuitId}. ` +
        this.translation.instant('+INSTALLATIONS.INSTALLATION.PANEL.HEATING_ZONE');
    } else {
      // indexing starts from 0 and Heating circuit should be displayed as 1. Heating Circuit
      displayedCircuitId = parseInt(circuitId, 10) + 1;
      defaultName =
        `${displayedCircuitId}. ` +
        this.translation.instant('+INSTALLATIONS.INSTALLATION.PANEL.CIRCUIT');
    }

    const remoteControllerHeating = ['.remoteController', 'heating.circuits.'];
    const areBothHeatingRemotePresent = remoteControllerHeating.every((item) =>
      circuitId.includes(item)
    );

    if (areBothHeatingRemotePresent) {
      return this.getHeatingRemoteCircuitName(circuitId);
    } else {
      return this.getCircuitNameFromStore(
        circuitId,
        displayedCircuitId,
        defaultName,
        nameWithoutIndex
      );
    }
  }

  private getHeatingRemoteCircuitName(circuitId: string): Observable<string> {
    let newCircuitId = circuitId;
    const remoteControllerHeating = ['.remoteController', 'heating.circuits.'];

    remoteControllerHeating.forEach((item) => {
      newCircuitId = newCircuitId.replace(item, '');
    });

    return of(
      '.' +
        this.translation.instant('+INSTALLATIONS.INSTALLATION.PANEL.CIRCUIT') +
        ' ' +
        newCircuitId
    );
  }

  private getCircuitNameFromStore(
    circuitId: string,
    displayedCircuitId: number,
    defaultName: string,
    nameWithoutIndex?: boolean
  ): Observable<string> {
    return new Observable((o: Subscriber<string>): void => {
      this.installationFeaturesStore
        .selectInstallationDeviceFeature(FeatureNames.circuitName(circuitId))
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(
          (feature: Feature<RenameHeatingCircuitProperties, RenameHeatingCircuitCommands>) => {
            if (isReady(feature)) {
              const circuitName = feature?.properties?.name?.value;
              if (nameWithoutIndex) {
                o.next(circuitName ? `${circuitName}` : defaultName);
              } else {
                o.next(circuitName ? `${displayedCircuitId}. ${circuitName}` : defaultName);
              }
            } else {
              o.next(defaultName);
            }
            o.complete();
          }
        );
    });
  }

  getRefrigerantCircuitName(refrigerantId: string): string {
    if (refrigerantId === null || refrigerantId === undefined) {
      return '-';
    }

    const idx = parseInt(refrigerantId, 10) + 1;

    return `${this.translation.instant(
      '+INSTALLATIONS.INSTALLATION.PANEL.REFRIGERANT_CIRCUIT'
    )} ${idx}`;
  }

  onDefrostingProcessChange(activated: boolean): void {
    this.defrostingWasStartedManually = activated;

    this.installationFeaturesStore.updateFeature(
      FeatureNames.outdoorDefrosting,
      activated,
      'active',
      '',
      activated ? FeatureCommands.activate : FeatureCommands.deactivate
    );
  }

  subscribeToDefrostingFeature(): void {
    this.installationFeaturesStore
      .selectInstallationDeviceFeature(FeatureNames.outdoorDefrosting)
      .pipe(
        takeUntil(this.ngUnsubscribe),
        filter(
          (feature: FeatureDeprecated<IsActiveProperty>) => !feature?.properties?.active?.value
        )
      )
      .subscribe(() => {
        if (this.defrostingWasStartedManually) {
          const data = {
            actions: [],
            message: this.translation.instant(
              '+INSTALLATIONS.INSTALLATION.HEATPUMP.STOP_DEFROSTING.SUCCESS_SNACKBAR.MESSAGE'
            ),
          };
          this.viToastService.info(data);
        }
      });
  }

  isSrcDeviceNotConnected(feature: Feature<DeviceConnectionStatus, never>): boolean {
    return (
      feature?.properties?.status?.value === DeviceConnectionStatusValues.paired ||
      feature?.properties?.status?.value === DeviceConnectionStatusValues.unknown
    );
  }

  resetSubscriptions(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.ngUnsubscribe = new Subject<void>();
  }

  setSelectedDeviceWithoutFetchingFeautures(deviceSelectId: string): void {
    this.devicesStore.setSelectedDeviceWithoutFetchingFeatures(deviceSelectId);
  }

  switchSelectionToMainDevice(): void {
    this.devicesStore.switchSelectionToMainDevice();
  }

  isAnyDeviceDisconnected(): Observable<UnassignableDeviceDisconnected> {
    return this.gatewayStore.unAssignableDevices$.pipe(
      switchMap((groupDevices) => {
        const repeaters: Observable<DeviceConnectionStatusValues>[] = [];
        const fhts: Observable<DeviceConnectionStatusValues>[] = [];
        groupDevices.forEach((group) => {
          group.devices.forEach((device) => {
            if (group.type === RemoteDeviceType.REPEATER) {
              repeaters.push(
                this.installationFeaturesStore
                  .selectInstallationDeviceFeature<DeviceConnectionStatus, never>(
                    FeatureNames.deviceConnection,
                    device.id
                  )
                  .pipe(map((data) => data?.properties?.status?.value))
              );
            }

            if (group.type === RemoteDeviceType.FHT) {
              fhts.push(
                this.installationFeaturesStore
                  .selectInstallationDeviceFeature<DeviceConnectionStatus, never>(
                    FeatureNames.deviceConnection,
                    device.id
                  )
                  .pipe(map((data) => data?.properties?.status?.value))
              );
            }
          });
        });

        return combineLatest([
          combineLatest(fhts).pipe(startWith([])),
          combineLatest(repeaters).pipe(startWith([])),
        ]);
      }),
      map(([fhts, repeaters]) => ({ fhts, repeaters })),
      map((res) => {
        const disconnectStatus = [
          DeviceConnectionStatusValues.paired,
          DeviceConnectionStatusValues.unknown,
        ];
        let isRepeaterDisconnected = false;
        let isFhtDisconnected = false;

        if (res) {
          if (
            res?.repeaters?.some((connectionValue) => disconnectStatus.includes(connectionValue))
          ) {
            isRepeaterDisconnected = true;
          }
          if (res?.fhts?.some((connectionValue) => disconnectStatus.includes(connectionValue))) {
            isFhtDisconnected = true;
          }
        }

        return { isRepeaterDisconnected, isFhtDisconnected };
      })
    );
  }

  isHeatingCircuitTargetFlowTemperatureVisible(
    circuitId: string,
    deviceId: string
  ): Observable<boolean> {
    const operatingModesActive$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
      IsStringValueProperty,
      never
    >(FeatureNames.circuitActiveMode(circuitId), deviceId);

    const operatingProgramsActive$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
      IsStringValueProperty,
      never
    >(FeatureNames.circuitActiveProgram(circuitId), deviceId);

    return combineLatest([operatingModesActive$, operatingProgramsActive$]).pipe(
      map(([modeFeature, programFeature]) => {
        const mode = modeFeature?.properties?.value?.value as DashboardHeatingOperatingModeValue;
        const program = programFeature?.properties?.value
          ?.value as HeatingCircuitHelper.HeatingOperatingProgramsSchema;

        return HeatingCircuitHelper.isCircuitTemperatureVisible(mode, program);
      })
    );
  }
}
